import React from 'react';
import { FiChevronLeft, FiMoreHorizontal, FiPlus } from 'react-icons/fi';
import { DashboardSidebar } from '@/layouts/dashboard';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { confirm } from '@/lib/confirm';
import { useClient } from 'jsonapi-react';
import { useTranslation } from 'react-i18next';
import { i18n } from '@/lib/i18n/i18n';
import usePermission from '@/hooks/usePermission';
import { useSession } from '@/hooks/useSession';
import { ModuleList, ModuleListItem, ModuleListTitle } from '@/components/custom/module';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';

export default function Sidebar(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const { exams, courses, selectedExam, setExamId, activeTab } = props;
  const [filteredExams, setFilteredExams] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const client = useClient();
  const [variable, setVariable] = useState();
  const currentUser = useSession().session.user;

  const { t } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage(process.env.REACT_APP_LOCALE);
  }, []);

  useEffect(() => {
    if (path.includes('/apostilas')) {
      setVariable(t('sideBar.questionBooks'));
    } else if (path.includes('/provas')) {
      setVariable(t('exams.toggleExams'));
    } else {
      setVariable(t('textsCommon.simulations'));
    }
  }, [path]);

  const filters = useFormik({
    initialValues: {
      course: 'blank'
    }
  });

  useEffect(() => {
    if (courses && courses?.length > 0) {
      let filter = exams;
      if (filters.values.course !== 'blank') {
        filter = exams.filter(item => item?.['course-ids']?.[0] === parseInt(filters.values.course));
      }
      setFilteredExams(filter);
    }

    if (searchTerm !== '') {
      const searchExam = exams.filter(item => {
        if (searchTerm === '') {
          return item;
        }
        if (item.title != null && item.title.toLowerCase().includes(searchTerm.toLocaleLowerCase())) {
          return item;
        }
      });
      setFilteredExams(searchExam);
    }
  }, [filters.values.course, exams, courses, searchTerm]);

  const handleDeleteExam = async examId => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteQuestionBook'),
      description: t('projects.warningDeleteCannotBeUndoneText'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      deleteExam(examId);
      setFilteredExams(exams.filter(item => item.id !== examId));
    }
  };

  const deleteExam = async examId => {
    try {
      await client.delete(['question_books', examId]);
      toast.success(t('toast.successDeleteExam'));
    } catch {
      toast.error(t('toast.errorDeleteBook'));
    }
  };

  const shortByCreation = (a, b) => {
    const dateA = new Date(a?.['created-at']);
    const dateB = new Date(b?.['created-at']);
    return dateB - dateA;
  };

  filteredExams?.sort(shortByCreation);

  return (
    <DashboardSidebar
      hasBrand={false}
      viewMode="create"
    >
      <nav className="notebook-sidebar__nav">
        <div className="filter-bar">
          <Link
            state={{ activeTab: activeTab }}
            to={path.includes('simulados/gerenciar') ? '/avaliacoes/simulados' : path.includes('/provas/gerenciar') ? '/avaliacoes/provas' : '/avaliacoes/apostilas'}
            className="filter-bar__back"
          >
            <div className="card__icon-wrapper">
              <FiChevronLeft />
            </div>
            <span className="filter-bar__back-container">
              <span className="filter-bar__title">{variable}</span>
            </span>
          </Link>
        </div>

        <div className="form__row">
          <label
            className="form__label"
            htmlFor="courseId"
          >
            {t('filter.labelSelectCourse')}
          </label>

          <select
            className="form__select"
            name="course"
            id="course"
            defaultValue="blank"
            value={filters.values.course}
            onChange={filters.handleChange}
          >
            <option value="blank">{t('filter.labelSelectCourse')}</option>
            {courses.map((item, index) => {
              return (
                <option
                  value={item.id}
                  key={index}
                >
                  {item.title}
                </option>
              );
            })}
          </select>
        </div>

        <div className="form__row">
          <input
            aria-label="Pesquisar módulos"
            className="form__control form__control--search-with-icon"
            placeholder={t('textsCommon.research')}
            type="search"
            name="moduleSearch"
            id="moduleSearch"
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        {variable === 'Apostilas' && (
          <Link
            className="btn btn--outline-dashed btn--small btn--full mb-4"
            to={'/apostilas/criar/nome'}
          >
            <FiPlus className="btn__icon" /> {t('exams.newQuestionBook')}
          </Link>
        )}
        {variable === 'Provas' && (
          <Link
            className="btn btn--outline-dashed btn--small btn--full mb-4"
            to={'/provas/criar/curso'}
          >
            <FiPlus className="btn__icon" /> {t('exams.newExam')}
          </Link>
        )}
        {variable === 'Simulados' && (
          <Link
            className="btn btn--outline-dashed btn--small btn--full mb-4"
            to={'/simulados/criar/curso'}
          >
            <FiPlus className="btn__icon" /> {t('exams.newSimulation')}
          </Link>
        )}

        <ModuleList>
          {filteredExams.map(item => {
            return (
              <ModuleListItem
                key={item.id}
                active={selectedExam?.id == item.id}
                onClick={() => {
                  if (variable === 'Provas') {
                    window.history.pushState({ id: item.id }, '', `/provas/gerenciar/${item.id}`);
                    navigate(`/provas/gerenciar/${item?.id}/${item?.['question-book-taxonomies']?.[0]?.id}`);
                  } else if (variable === 'Apostilas') {
                    window.history.pushState({ id: item.id }, '', `/apostilas/gerenciar/${item.id}`);
                    navigate(`/apostilas/gerenciar/${item?.id}/${item?.['question-book-taxonomies']?.[0]?.id}`);
                  } else {
                    window.history.pushState({ id: item.id }, '', `/simulados/gerenciar/${item.id}`);
                    navigate(`/simulados/gerenciar/${item?.id}/${item?.['question-book-taxonomies']?.[0]?.id}`);
                  }
                  setExamId(item.id);
                }}
              >
                <ModuleListTitle>{item.title}</ModuleListTitle>

                {currentUser.id === item.creator?.id && (
                  <div onClick={e => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button">
                          <FiMoreHorizontal size={16} />
                        </button>
                      </DropdownMenuTrigger>

                      <DropdownMenuContent
                        align="end"
                        style={{ zIndex: 9999 }}
                      >
                        {variable === 'Apostilas' && usePermission('Editar exercícios', 'Questões e exercícios') ? (
                          <DropdownMenuItem
                            onSelect={() => {
                              navigate(`/apostilas/criar/nome/${item.id}`);
                            }}
                          >
                            {t('button.edit')}
                          </DropdownMenuItem>
                        ) : null}
                        {variable === 'Provas' && usePermission('Editar provas', 'Provas e simulados') ? (
                          <DropdownMenuItem
                            onSelect={() => {
                              navigate(`/provas/criar/curso/${item.id}`);
                            }}
                          >
                            {t('button.edit')}
                          </DropdownMenuItem>
                        ) : null}
                        {variable === 'Simulados' && usePermission('Editar simulados', 'Provas e simulados') ? (
                          <DropdownMenuItem
                            onSelect={() => {
                              navigate(`/simulados/criar/curso/${item.id}`);
                            }}
                          >
                            {t('button.edit')}
                          </DropdownMenuItem>
                        ) : null}
                        {usePermission('Excluir exercícios', 'Questões e exercícios') && (
                          <DropdownMenuItem
                            onSelect={() => {
                              handleDeleteExam(item.id);
                            }}
                            className="text-danger"
                          >
                            {t('button.delete')}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </ModuleListItem>
            );
          })}
        </ModuleList>
      </nav>
    </DashboardSidebar>
  );
}
